/**
 * SanitairLuxe — Add-to-Cart Success Modal
 * Opens a popup after product is added to cart, showing:
 *   - Added product info (image, title, variant, price)
 *   - Cart summary (item count + total)
 *   - "Continue shopping" + "Go to cart" buttons
 *   - Recommended products carousel (complementary first, related as fallback)
 *
 * Hooks into:
 *   - product-form (theme base) via renderContents override
 *   - sl:cart:added (SL custom product pages)
 *   - cart:item:added (quick-add / card add)
 */

(function () {
  'use strict';

  /* ---- DOM refs ---- */
  const overlay = document.getElementById('SlCartModalOverlay');
  const modal   = document.getElementById('SlCartModal');

  if (!overlay || !modal) return;

  const closeBtn     = modal.querySelector('.sl-cart-modal__close');
  const titleEl      = modal.querySelector('.sl-cart-modal__title');
  const subtitleEl   = modal.querySelector('.sl-cart-modal__subtitle');
  const productImg   = modal.querySelector('.sl-cart-modal__product-img img');
  const productName  = modal.querySelector('.sl-cart-modal__product-name');
  const productVendor = modal.querySelector('.sl-cart-modal__product-vendor');
  const productVariant = modal.querySelector('.sl-cart-modal__product-variant');
  const summaryCount = modal.querySelector('[data-cart-count]');
  const summaryTotal = modal.querySelector('[data-cart-total]');
  const cartLink     = modal.querySelector('.sl-cart-modal__btn--cart');
  const recsTrack    = modal.querySelector('.sl-cart-modal__recs-track');
  const recsLoading  = modal.querySelector('.sl-cart-modal__recs-loading');
  const recsSection  = modal.querySelector('.sl-cart-modal__recs');
  const recsPrev     = modal.querySelector('.sl-cart-modal__recs-arrow--prev');
  const recsNext     = modal.querySelector('.sl-cart-modal__recs-arrow--next');

  const subtitleText = modal.dataset.subtitleText || 'is toegevoegd aan je winkelwagen';
  const summaryCountPrefix = modal.dataset.summaryCountPrefix || 'Er zitten';
  const summaryCountSuffix = modal.dataset.summaryCountSuffix || 'artikelen in je winkelwagen';
  const summaryCountSuffixOne = modal.dataset.summaryCountSuffixOne || summaryCountSuffix;
  const summaryTotalPrefix = modal.dataset.summaryTotalPrefix || 'Winkelwagen totaal';

  let isOpen = false;
  let lastProductId = null;

  /* ==================================================================
     OPEN / CLOSE
     ================================================================== */

  function openModal() {
    if (isOpen) return;
    isOpen = true;
    overlay.classList.add('is-open');
    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    if (!isOpen) return;
    isOpen = false;
    overlay.classList.remove('is-open');
    modal.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  // Close triggers
  closeBtn.addEventListener('click', closeModal);
  overlay.addEventListener('click', closeModal);

  // "Ga verder" (continue shopping) button closes the modal
  const continueBtn = modal.querySelector('.sl-cart-modal__btn--continue');
  if (continueBtn) {
    continueBtn.addEventListener('click', closeModal);
  }

  // "Naar winkelwagen" button closes modal and opens cart drawer
  const cartBtn = modal.querySelector('.sl-cart-modal__btn--cart');
  if (cartBtn) {
    cartBtn.addEventListener('click', () => {
      closeModal();
      // Open cart drawer
      const cartDrawer = document.getElementById('Cart-Drawer');
      if (cartDrawer) {
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          cartDrawer.classList.add('active');
          document.body.classList.add('open-cc');
        }
      } else {
        // Fallback: try SL-style cart drawer
        const slDrawer = document.querySelector('.side-panel--cart');
        if (slDrawer) {
          slDrawer.classList.add('active');
          document.body.classList.add('open-cart');
        } else {
          // Last resort: go to cart page
          window.location.href = theme?.routes?.cart_url || '/cart';
        }
      }
    });
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) closeModal();
  });

  /* ==================================================================
     POPULATE MODAL WITH PRODUCT DATA
     ================================================================== */

  function populateModal(item) {
    const setBuilder = document.querySelector('[data-slcsb-section]');
    const setHeading = document.querySelector('h1');
    const plateSelect = setBuilder && setBuilder.querySelector('[data-slcsb-plate-select]');
    if (plateSelect && setHeading && (item.product_title || item.title || '').trim() === setHeading.textContent.trim()) {
      const plateOption = plateSelect.selectedOptions[0];
      const plateCheck = plateSelect.closest('[data-slcsb-item]').querySelector('[data-slcsb-checkbox]');
      item = { ...item, variant_title: plateCheck.checked ? 'Gekozen drukplaat: ' + plateOption.dataset.label + '. De setfoto toont de witte standaarduitvoering.' : 'Geen drukplaat geselecteerd.' };
    }
    // item = line item returned from /cart/add.js
    // or a manually-constructed object

    if (titleEl) {
      titleEl.textContent = item.product_title || item.title || '';
    }
    if (subtitleEl) {
      subtitleEl.textContent = subtitleText;
    }
    if (productImg && item.image) {
      let imgSrc = item.image;
      // Shopify may return protocol-relative URLs
      if (imgSrc.startsWith('//')) imgSrc = 'https:' + imgSrc;
      // Resize to 300px
      if (imgSrc.includes('cdn.shopify.com')) {
        imgSrc = imgSrc.replace(/(\.[a-z]+)\?/, '_300x$1?');
        // Alternative pattern
        if (!imgSrc.includes('_300x')) {
          imgSrc = imgSrc.replace(/\?v=/, '_300x.jpg?v=').replace(/(\.[a-z]+)$/, '_300x$1');
        }
      }
      productImg.src = imgSrc;
      productImg.alt = item.product_title || '';
    } else if (productImg) {
      productImg.src = '';
    }

    if (productName) {
      productName.textContent = item.product_title || item.title || '';
    }

    if (productVendor) {
      productVendor.textContent = item.vendor || '';
      productVendor.style.display = item.vendor ? '' : 'none';
    }

    if (productVariant) {
      const variantTitle = item.variant_title || '';
      productVariant.textContent = variantTitle;
      productVariant.style.display = variantTitle && variantTitle !== 'Default Title' ? '' : 'none';
    }

    // Store product ID for recommendations
    // /cart/add.js returns product_id, product JSON uses id
    lastProductId = item.product_id || item.id || null;
  }

  /* ==================================================================
     UPDATE CART SUMMARY (fetch /cart.js)
     ================================================================== */

  function updateCartSummary() {
    fetch((theme?.routes?.cart_url || '/cart') + '.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(r => r.json())
      .then(cart => {
        if (summaryCount) {
          const countSuffix = cart.item_count === 1 ? summaryCountSuffixOne : summaryCountSuffix;
          summaryCount.innerHTML = summaryCountPrefix + ' <strong>' + cart.item_count + ' ' + countSuffix + '</strong>';
        }
        if (summaryTotal) {
          summaryTotal.innerHTML = summaryTotalPrefix + ': <strong>' + formatMoney(cart.total_price) + '</strong>';
        }
      })
      .catch(() => {});
  }

  /* ==================================================================
     RECOMMENDATIONS
     Prefer complementary products (real accessories, configured in
     Search & Discovery). Fall back to related products when none exist.
     ================================================================== */

  function recommendationsUrl(productId, intent) {
    let rootUrl = '';
    try { rootUrl = theme?.routes?.root_url || ''; } catch(e) {}
    // Remove trailing slash to avoid double slashes
    rootUrl = rootUrl.replace(/\/$/, '');
    return `${rootUrl}/recommendations/products.json?product_id=${productId}&limit=10&intent=${intent}`;
  }

  function fetchRecommendations(productId, intent) {
    return fetch(recommendationsUrl(productId, intent))
      .then(r => {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(data => (data.products || []));
  }

  function loadRecommendations(productId) {
    if (!recsTrack) return;
    if (!productId) {
      console.warn('[SLCartModal] No product_id for recommendations');
      if (recsSection) recsSection.style.display = 'none';
      return;
    }

    // Show loading skeletons
    if (recsLoading) recsLoading.style.display = 'flex';
    recsTrack.innerHTML = '';
    if (recsSection) recsSection.style.display = '';

    fetchRecommendations(productId, 'complementary')
      .then(products => {
        if (products.length) return products;
        // No complementary products configured for this product
        return fetchRecommendations(productId, 'related');
      })
      .then(products => {
        if (recsLoading) recsLoading.style.display = 'none';

        if (!products.length) {
          if (recsSection) recsSection.style.display = 'none';
          return;
        }

        recsTrack.innerHTML = products.map(p => buildRecCard(p)).join('');
        updateArrows();
      })
      .catch(() => {
        if (recsLoading) recsLoading.style.display = 'none';
        if (recsSection) recsSection.style.display = 'none';
      });
  }

  function buildRecCard(product) {
    const img = product.featured_image
      ? product.featured_image.replace(/(\.[a-z]+)\?/, '_400x$1?')
      : '';
    const priceHtml = formatMoney(product.price);
    let comparePriceHtml = '';
    if (product.compare_at_price && product.compare_at_price > product.price) {
      comparePriceHtml = '<span class="sl-cart-modal__rec-card__price--compare">' + formatMoney(product.compare_at_price) + '</span>';
    }

    return `
      <a href="${product.url}" class="sl-cart-modal__rec-card">
        <div class="sl-cart-modal__rec-card__img">
          ${img ? '<img src="' + img + '" alt="' + escapeHtml(product.title) + '" loading="lazy" width="170" height="170">' : ''}
        </div>
        <div class="sl-cart-modal__rec-card__title">${escapeHtml(product.title)}</div>
        <div class="sl-cart-modal__rec-card__price">${priceHtml}${comparePriceHtml}</div>
      </a>
    `;
  }

  /* ---- Carousel arrows ---- */
  function updateArrows() {
    if (!recsTrack || !recsPrev || !recsNext) return;
    const maxScroll = recsTrack.scrollWidth - recsTrack.clientWidth;
    recsPrev.classList.toggle('is-hidden', recsTrack.scrollLeft < 10);
    recsNext.classList.toggle('is-hidden', recsTrack.scrollLeft >= maxScroll - 10);
  }

  if (recsTrack) {
    recsTrack.addEventListener('scroll', updateArrows, { passive: true });
  }
  if (recsPrev) {
    recsPrev.addEventListener('click', () => {
      recsTrack.scrollBy({ left: -200, behavior: 'smooth' });
    });
  }
  if (recsNext) {
    recsNext.addEventListener('click', () => {
      recsTrack.scrollBy({ left: 200, behavior: 'smooth' });
    });
  }

  /* ==================================================================
     FORMAT MONEY HELPER
     ================================================================== */

  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents, 10);
    if (isNaN(cents)) return '';
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    // Use a thousands separator
    const parts = amount.split(',');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const formatted = parts.join(',');
    return '€ ' + formatted;
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  /* ==================================================================
     SHOW MODAL (main entry point)
     ================================================================== */

  function showCartModal(item) {
    populateModal(item);
    updateCartSummary();
    loadRecommendations(item.product_id);
    openModal();
  }

  // Expose globally so other scripts can trigger it
  window.SLCartModal = { show: showCartModal, close: closeModal };

  /* ==================================================================
     HOOK 1: SL custom product pages (sl:cart:added)
     ================================================================== */

  document.addEventListener('sl:cart:added', (e) => {
    const item = e.detail || {};
    showCartModal(item);
  });

  /* ==================================================================
     HOOK 2: Theme base product-form (override renderContents)
     We patch ProductForm.prototype to intercept after add-to-cart,
     show our modal instead of opening the cart drawer.
     ================================================================== */

  function patchThemeProductForm() {
    const PF = customElements.get('product-form');
    if (!PF) return;

    const origRender = PF.prototype.renderContents;
    if (!origRender) return;

    PF.prototype.renderContents = function (parsedState) {
      const form = this.querySelector('form') || this.form || null;
      // Still update cart drawer / bubble HTML behind the scenes
      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (!el) return;
        const target = el.querySelector(section.selector) || el;
        target.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      });

      // Re-init cart drawer if needed
      if (typeof CartDrawer !== 'undefined') {
        new CartDrawer();
      }
      if (typeof Cart !== 'undefined') {
        new Cart().renderContents(parsedState);
      }

      // Close product drawer if adding from quick-view
      let product_drawer = document.getElementById('Product-Drawer');
      if (product_drawer && product_drawer.contains(this)) {
        product_drawer.classList.remove('active');
        document.body.classList.remove('open-cc');
      }

      // Build item data from the response
      const item = parsedState.hasOwnProperty('product_title')
        ? parsedState
        : (parsedState.items ? parsedState.items[0] : null);

      if (item && (item.product_id || item.id)) {
        showCartModal(item);
      } else {
        // parsedState is a sections response without product data
        // Fetch cart to get the last added item (first in cart.items)
        fetch((theme?.routes?.cart_url || '/cart') + '.js', {
          headers: { 'Accept': 'application/json' }
        })
          .then(r => r.json())
          .then(cart => {
            if (cart.items && cart.items.length > 0) {
              // Prefer the variant that was just submitted; Shopify lists newest lines first
              let addedId = null;
              try {
                const idInput = form && form.querySelector('[name="id"]');
                addedId = idInput ? parseInt(idInput.value, 10) : null;
              } catch (e) {}
              const match = addedId ? cart.items.find(i => i.variant_id === addedId) : null;
              showCartModal(match || cart.items[0]);
            }
          })
          .catch(() => {});
      }
    };
  }

  /**
   * Patch ProductCard (app.js) to show modal instead of cart drawer
   */
  function patchProductCard() {
    const PC = customElements.get('product-card');
    if (!PC) return;

    const origRender = PC.prototype.renderContents;
    if (!origRender) return;

    PC.prototype.renderContents = function (parsedState) {
      // Update cart sections HTML silently
      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (!el) return;
        const target = el.querySelector(section.selector) || el;
        target.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        if (typeof CartDrawer !== 'undefined') {
          new CartDrawer();
        }
        if (typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      });

      // Do NOT open cart drawer — the modal will be triggered
      // by the cart:item-added event listener instead
    };
  }

  /* ==================================================================
     HOOK 3: Quick-add / product-card simple add (cart:item-added)
     (dispatched by ProductCard in app.js)
     ================================================================== */

  document.addEventListener('cart:item-added', (e) => {
    const detail = e.detail || {};
    const item = detail.product || detail;
    if (item && (item.product_title || item.title)) {
      showCartModal(item);
    } else {
      // Fallback: fetch the cart and show the last added item
      fetch((theme?.routes?.cart_url || '/cart') + '.js', {
        headers: { 'Accept': 'application/json' }
      })
        .then(r => r.json())
        .then(cart => {
          if (cart.items && cart.items.length > 0) {
            showCartModal(cart.items[0]);
          }
        })
        .catch(() => {});
    }
  });

  /* ==================================================================
     INIT — patch after DOM ready
     ================================================================== */

  function initPatches() {
    patchThemeProductForm();
    patchProductCard();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPatches);
  } else {
    initPatches();
  }

  // Re-patch on Shopify theme editor reload
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', initPatches);
  }

})();
